import {Component, Input, inject} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {DashboardOverviewStats, PureOriginalMatchTarget} from '../../models/dashboard-statistics.model';
import {DashboardPeriodService} from '../../../dashboard/services/dashboard-period.service';

interface KpiCardConfig {
  label: string;
  value: number;
  delta: number | null;
  subtitle: string | null;
  color: string;          // primary text/sparkline color
  glowColor: string;      // halo glow rgba
  badgeBg: string;
  iconText?: string;
  iconSvgPath?: string;
  sparklinePath?: string;
  sparklineFillId?: string;
  lowerIsBetter?: boolean;
  showSparkline?: boolean;
  valueColor?: string;
}

@Component({
  selector: 'app-overview-kpi-cards',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './overview-kpi-cards.component.html',
  styleUrls: ['./overview-kpi-cards.component.css']
})
export class OverviewKpiCardsComponent {
  @Input() overview: DashboardOverviewStats | null = null;
  @Input() previousOverview: DashboardOverviewStats | null = null;
  @Input() isLoading: boolean = false;

  private readonly router = inject(Router);
  private readonly periodService = inject(DashboardPeriodService);

  // ── Navigation ─────────────────────────────────────────────

  openImports(): void {
    const {start, end} = this.periodService.todayUntilNow();
    this.router.navigate(['/app/file-service'], {
      queryParams: {startDate: start, endDate: end}
    });
  }

  openSuccessful(): void {
    this.router.navigate(['/app/file-service'], {
      queryParams: {
        status: 'Successful',
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd()
      }
    });
  }

  openPartial(): void {
    this.router.navigate(['/app/file-service'], {
      queryParams: {
        status: 'Partially tuned',
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd()
      }
    });
  }

  openFailed(): void {
    this.router.navigate(['/app/file-service'], {
      queryParams: {
        status: 'Failed',
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd()
      }
    });
  }

  openPureOriginals(): void {
    const {start, end} = this.periodService.pureOriginalRange();
    this.router.navigate(['/app/pure-originals/uploaded'], {
      queryParams: {
        startDate: start,
        endDate: end,
        granularity: this.periodService.trendGranularity(),
        pageNumber: 0,
        pageSize: 50
      }
    });
  }

  openMasterDatabase(): void {
    const {start, end} = this.periodService.masterRange();
    this.router.navigate(['/app/master-database/uploaded'], {
      queryParams: {
        startDate: start,
        endDate: end,
        granularity: this.periodService.trendGranularity(),
        pageNumber: 0,
        pageSize: 50
      }
    });
  }

  canOpenMatches(target: PureOriginalMatchTarget): boolean {
    const count = target === 'missing' ? this.poMissing : target === 'partial' ? this.poPartial : this.poSuccessful;
    return !this.isLoading && count > 0;
  }

  openMatches(target: PureOriginalMatchTarget): void {
    if (!this.canOpenMatches(target)) return;
    this.router.navigate(['/app/pure-originals/matches', target], {
      queryParams: {
        startDate: this.periodService.currentStart(),
        endDate: this.periodService.currentEnd(),
        pageNumber: 0,
        pageSize: 50,
        sortAttribute: 'pureOriginalBaseId',
        sortDirection: 'desc'
      }
    });
  }

  // Sparkline reference paths — visual placeholders mirroring V3 design.
  // Charts populate from real timestamp data via the activity-trends section below.
  readonly sparkPaths = {
    purpleUp:   'M0,28 L24,22 L48,26 L72,18 L96,20 L120,12 L144,16 L168,8 L192,14 L216,6 L240,10',
    greenUp:    'M0,30 L24,26 L48,28 L72,20 L96,24 L120,16 L144,12 L168,18 L192,10 L216,14 L240,8',
    amberFlat:  'M0,18 L24,22 L48,16 L72,20 L96,14 L120,18 L144,22 L168,16 L192,20 L216,18 L240,22',
    redUp:      'M0,24 L24,20 L48,22 L72,16 L96,12 L120,18 L144,14 L168,10 L192,16 L216,12 L240,8'
  };

  readonly sparkFills = {
    purpleUp:   'M0,28 L24,22 L48,26 L72,18 L96,20 L120,12 L144,16 L168,8 L192,14 L216,6 L240,10 L240,36 L0,36 Z',
    greenUp:    'M0,30 L24,26 L48,28 L72,20 L96,24 L120,16 L144,12 L168,18 L192,10 L216,14 L240,8 L240,36 L0,36 Z',
    amberFlat:  'M0,18 L24,22 L48,16 L72,20 L96,14 L120,18 L144,22 L168,16 L192,20 L216,18 L240,22 L240,36 L0,36 Z',
    redUp:      'M0,24 L24,20 L48,22 L72,16 L96,12 L120,18 L144,14 L168,10 L192,16 L216,12 L240,8 L240,36 L0,36 Z'
  };

  // ── Delta helpers ──────────────────────────────────────────

  delta(current: number | undefined, previous: number | undefined): number | null {
    if (current == null || previous == null) return null;
    if (previous === 0) {
      if (current === 0) return 0;
      return null;
    }
    return Math.round(((current - previous) / previous) * 1000) / 10;
  }

  /** delta -> 'up' | 'down' | 'flat' (for green/red/grey pill) */
  deltaPolarity(delta: number | null, lowerIsBetter = false): 'up' | 'down' | 'flat' | 'none' {
    if (delta == null) return 'none';
    if (delta === 0) return 'flat';
    const positive = delta > 0;
    const good = lowerIsBetter ? !positive : positive;
    return good ? 'up' : 'down';
  }

  formatDelta(delta: number | null): string {
    if (delta == null) return '';
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
    return `${sign}${Math.abs(delta).toFixed(1)}%`;
  }

  // ── Convenience getters for template ───────────────────────

  get totalImports() { return this.overview?.totalImports ?? 0; }
  get importsSuccessful() { return this.overview?.importsSuccessful ?? 0; }
  get importsPartial() { return this.overview?.importsPartial ?? 0; }
  get importsFailed() { return this.overview?.importsFailed ?? 0; }
  get totalPureOriginal() { return this.overview?.totalPureOriginal ?? 0; }
  get poMissing() { return this.overview?.pureOriginalMatchingMissing ?? 0; }
  get poPartial() { return this.overview?.pureOriginalMatchingPartial ?? 0; }
  get poSuccessful() { return this.overview?.pureOriginalMatchingSuccessful ?? 0; }
  get totalMaster() { return this.overview?.totalMaster ?? 0; }

  get totalImportsDelta() { return this.delta(this.overview?.totalImports, this.previousOverview?.totalImports); }
  get successfulDelta() { return this.delta(this.overview?.importsSuccessful, this.previousOverview?.importsSuccessful); }
  get partialDelta() { return this.delta(this.overview?.importsPartial, this.previousOverview?.importsPartial); }
  get failedDelta() { return this.delta(this.overview?.importsFailed, this.previousOverview?.importsFailed); }
  get poDelta() { return this.delta(this.overview?.totalPureOriginal, this.previousOverview?.totalPureOriginal); }
  get poMissingDelta() { return this.delta(this.overview?.pureOriginalMatchingMissing, this.previousOverview?.pureOriginalMatchingMissing); }
  get poPartialDelta() { return this.delta(this.overview?.pureOriginalMatchingPartial, this.previousOverview?.pureOriginalMatchingPartial); }
  get poSuccessfulDelta() { return this.delta(this.overview?.pureOriginalMatchingSuccessful, this.previousOverview?.pureOriginalMatchingSuccessful); }
  get masterDelta() { return this.delta(this.overview?.totalMaster, this.previousOverview?.totalMaster); }

  get successRate(): string {
    if (!this.overview || !this.overview.totalImports) return '0';
    return ((this.overview.importsSuccessful / this.overview.totalImports) * 100).toFixed(0);
  }

  get partialRate(): string {
    if (!this.overview || !this.overview.totalImports) return '0';
    return ((this.overview.importsPartial / this.overview.totalImports) * 100).toFixed(0);
  }

  get failedRate(): string {
    if (!this.overview || !this.overview.totalImports) return '0';
    return ((this.overview.importsFailed / this.overview.totalImports) * 100).toFixed(0);
  }

  get poTotal(): number {
    return this.poMissing + this.poPartial + this.poSuccessful || 1;
  }

  get poMissingPct(): string {
    return ((this.poMissing / this.poTotal) * 100).toFixed(1);
  }

  get poPartialPct(): string {
    return ((this.poPartial / this.poTotal) * 100).toFixed(1);
  }

  get poSuccessfulPct(): string {
    return ((this.poSuccessful / this.poTotal) * 100).toFixed(1);
  }
}
